import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Cross, Star, Bell, CalendarDays, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useSiteLayout } from '../context/SiteLayoutContext';
import { PALETTE_KEYS, PALETTE_VARS, resolveColor, isDark } from './site-layout/palette';
import { track } from '../utils/analytics';

const ICONS = { cross: Cross, star: Star, bell: Bell, calendar: CalendarDays };
const DISMISS_KEY = 'demarre-banner-dismissed';

export const bannerPageKey = (path) => {
  if (path === '/' || path === '/faq') return 'home';
  if (path.startsWith('/pilgrimages')) return 'pilgrimages';
  if (path.startsWith('/tour/')) return 'tour';
  if (path.startsWith('/faqs')) return 'faq';
  if (path.startsWith('/lead-a-group')) return 'lead_group';
  if (path.startsWith('/galeria')) return 'gallery';
  return 'other';
};
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
export const bannerSignature = (b) => [b.text_pt, b.text_en, b.text_es, b.link_url].join('|');

const readDismissed = () => { try { const v = JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]'); return Array.isArray(v) ? v : [v]; } catch { return []; } };
const tourIdOf = (path) => (path.startsWith('/tour/') ? path.split('/')[2] : '');

// Announcement strips above the navbar; several active ones rotate every ~6s. Publishes --site-banner-h.
export const SiteBanner = () => {
  const { layout, isPreview } = useSiteLayout();
  const { language } = useLanguage();
  const { pathname } = useLocation();
  const ref = useRef(null);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const page = bannerPageKey(pathname);
  const tourId = tourIdOf(pathname);
  const d = today();
  const textOf = (b) => (b[`text_${language}`] || b.text_pt || '').trim();
  const pinned = isPreview && layout.preview_banner_id ? (layout.banners || []).filter(b => b.id === layout.preview_banner_id) : null;
  const active = (pinned || (layout.banners || []).filter(b => b.enabled
    && (b.pages || []).includes(page)
    && (page !== 'tour' || !(b.tour_ids || []).length || b.tour_ids.includes(tourId))
    && (isPreview || ((!b.starts_at || d >= b.starts_at) && (!b.ends_at || d <= b.ends_at)))
    && (isPreview || !b.dismissible || !dismissed.includes(bannerSignature(b)))))
    .filter(b => textOf(b));
  const count = active.length;
  const b = active[idx % Math.max(count, 1)];
  const show = !!b;

  useEffect(() => {
    if (count < 2 || paused) return undefined;
    const t = setInterval(() => setIdx(i => i + 1), 6000);
    return () => clearInterval(t);
  }, [count, paused]);

  const shownId = b?.id;
  useEffect(() => { if (shownId) track('banner_view', { target: shownId }); }, [shownId, pathname]);

  useLayoutEffect(() => {
    const root = document.documentElement.style;
    if (!show || !ref.current) { root.setProperty('--site-banner-h', '0px'); return undefined; }
    const ro = new ResizeObserver(([e]) => root.setProperty('--site-banner-h', `${Math.ceil(e.target.offsetHeight)}px`));
    ro.observe(ref.current);
    return () => { ro.disconnect(); root.setProperty('--site-banner-h', '0px'); };
  }, [show]);

  if (!show) return null;
  const sig = bannerSignature(b);
  const bg = PALETTE_KEYS.includes(b.bg) ? `var(${PALETTE_VARS[b.bg]})` : (b.bg || 'var(--site-navy)');
  const tone = b.text || (isDark(resolveColor(b.bg || 'navy', layout.palette)) ? 'light' : 'dark');
  const color = tone === 'light' ? '#ffffff' : 'var(--site-text)';
  const Icon = ICONS[b.icon];
  const label = (b[`link_label_${language}`] || '').trim() || (language === 'en' ? 'Learn more' : language === 'es' ? 'Saber más' : 'Saiba mais');
  const linkCls = 'inline-flex items-center gap-1 font-semibold underline underline-offset-4 decoration-1 hover:opacity-80 whitespace-nowrap';
  const close = () => { track('banner_close', { target: b.id }); const next = [...dismissed.filter(x => x !== sig), sig].slice(-30); try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch {} setDismissed(next); };
  return (
    <div ref={ref} className="relative text-[12px] sm:text-[13px] leading-snug transition-colors duration-500" style={{ background: bg, color, fontFamily: '"Inter", system-ui, sans-serif' }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="site-banner" data-banner-id={b.id}>
      <div key={b.id} className="max-w-7xl mx-auto px-10 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center animate-in fade-in duration-500">
        {Icon && <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: tone === 'light' ? 'var(--site-gold)' : 'currentColor' }} />}
        <span data-testid="site-banner-text">{textOf(b)}</span>
        {b.link_url && (b.link_url.startsWith('/')
          ? <Link to={b.link_url} onClick={() => track('banner_click', { target: b.id })} className={linkCls} style={{ color }} data-testid="site-banner-link">{label} <ArrowRight className="w-3 h-3" /></Link>
          : <a href={b.link_url} onClick={() => track('banner_click', { target: b.id })} target="_blank" rel="noopener noreferrer" className={linkCls} style={{ color }} data-testid="site-banner-link">{label} <ArrowRight className="w-3 h-3" /></a>)}
      </div>
      {count > 1 && (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 flex gap-1" data-testid="site-banner-dots">
          {active.map((x, i) => (
            <button key={x.id} type="button" onClick={() => setIdx(i)} aria-label={`Aviso ${i + 1}`} data-testid={`site-banner-dot-${i}`}
              className="p-1.5 -m-0.5 flex items-center justify-center">
              <span className="block w-1.5 h-1.5 rounded-full transition-opacity" style={{ background: color, opacity: i === idx % count ? 1 : 0.35 }} />
            </button>
          ))}
        </div>
      )}
      {b.dismissible && (
        <button type="button" onClick={close} aria-label="Fechar aviso" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 opacity-70 hover:opacity-100" style={{ color }} data-testid="site-banner-close">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};
